import { EllipsedText, isValidLatitude, isValidLongitude, useMoment } from '@gorila-shared-ui/components';
import { StyledLink } from 'baseui/link';
import { StyledCell, StyledRow } from 'baseui/table';
import { CSSProperties, memo } from 'react';
import { areEqual, ListChildComponentProps } from 'react-window';
import { useRecoilState } from 'recoil';
import {
  DOOR_ADDRESS_COLUMN_SIZE,
  DOOR_DATE_COLUMN_MAX_WIDTH,
  DOOR_DATE_COLUMN_SIZE,
  DOOR_DELIVERY_FENCE_COLUMN_SIZE,
  DOOR_EVENT_COLUMN_SIZE,
  DOOR_IDENT_COLUMN_SIZE,
  DOOR_LABEL_COLUMN_SIZE,
} from '../../../constants/reports';
import { useCamera } from '../../../hooks/useCamera';
import { useStyles } from '../../../hooks/useStyles';
import { selectedReportRowState } from '../../../storage/reports';
import { HistoricDoorState } from '../../../types/currentState';
import { isDevice } from '../../../utils/currentStates';

function DoorStateReportRow({
  data,
  index,
  style,
}: Readonly<
  ListChildComponentProps<{
    states: HistoricDoorState[];
    extended?: boolean;
  }>
>) {
  const state = data.states[index];
  const extended = data.extended;
  const [selected, setSelected] = useRecoilState(selectedReportRowState);
  const { theme, css, styles } = useStyles();
  const { formatDateFromTs } = useMoment();
  const { zoomTo } = useCamera();

  const onClick = () => {
    const { position_latitude: lat, position_longitude: lng } = state;
    setSelected(index);
    if (lat && lng && isValidLatitude(lat) && isValidLongitude(lng)) {
      zoomTo({ lat, lng }, 18);
    }
  };

  return (
    <StyledRow
      key={index}
      style={{ ...(style as CSSProperties), cursor: 'pointer' }}
      className={css({
        backgroundColor:
          selected === index
            ? theme.colors.gray100 // Active color
            : (index + 1) % 2 === 0
              ? theme.colors.tableStripedBackground
              : '',
        ':hover': {
          background: theme.colors.buttonTertiaryHover,
        },
      })}
      onClick={onClick}
    >
      {extended && (
        <StyledCell
          style={{
            flex: DOOR_IDENT_COLUMN_SIZE,
            ...styles.smallTableCell,
          }}
        >
          {isDevice(state.asset.device) ? state.asset.device.ident : '-'}
        </StyledCell>
      )}
      {extended && (
        <StyledCell
          style={{
            flex: DOOR_LABEL_COLUMN_SIZE,
            ...styles.smallTableCell,
          }}
        >
          {state.asset.label || '-'}
        </StyledCell>
      )}
      <StyledCell
        style={{
          flex: DOOR_DATE_COLUMN_SIZE,
          maxWidth: DOOR_DATE_COLUMN_MAX_WIDTH,
          ...styles.smallTableCell,
        }}
      >
        {formatDateFromTs(+state.timestamp)}
      </StyledCell>
      <StyledCell
        style={{
          flex: DOOR_EVENT_COLUMN_SIZE,
          ...styles.smallTableCell,
        }}
      >
        {state.asset.isDoorOpen ? 'Apertura' : 'Cierre'}
      </StyledCell>
      <StyledCell
        style={{
          flex: DOOR_DELIVERY_FENCE_COLUMN_SIZE,
          ...styles.smallTableCell,
        }}
      >
        {state.asset.deliveryFence?.name || '-'}
      </StyledCell>
      <StyledCell style={{ flex: DOOR_ADDRESS_COLUMN_SIZE, ...styles.smallTableCell }}>
        <StyledLink
          href={`https://maps.google.com/?q=${state.position_latitude},${state.position_longitude}`}
          target="_blank"
          style={{ ...theme.typography.ParagraphXSmall }}
        >
          <EllipsedText
            inline
            breakAll
          >
            {state.geoReference}
          </EllipsedText>
        </StyledLink>
      </StyledCell>
    </StyledRow>
  );
}

export default memo(DoorStateReportRow, areEqual);
